import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import LabelName from 'components/UI/LabelName/LabelName';
import { Tooltip } from 'primereact/tooltip';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { LoaderComponent } from 'shared/LoaderComponent';
import { totalCountEvent } from 'services/generalServices';
import { listUsers } from 'services/users/userServices';
import { sortByKey } from 'utils/utils';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { markEmailsAsRead } from 'services/mailbox/mailboxService';
import { MAPPING_STATUSES } from 'constants/mappingStatus';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { useNavbarContext } from 'context/NavbarContext';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

export default function ReconcileHeader({
  isGlobalMail,
  caseValue,
  dateFilter,
  onDateChange,
  refreshEmails,
  setShowComposeModal,
  selectedEmails,
  setShow,
  setSelectedEmailThreadPkSk,
  filters,
  setFilters,
  mapping_statuses,
  setSelectedEmails,
}) {
  const history = useHistory();
  const [userList, setUserList] = useState([]);
  const [isFirmUserLoading, setIsFirmUserLoading] = useState(false);
  const { userDetails } = useUserDetailsContext();
  const { notificationData } = useNavbarContext();
  const calendarRef = useRef(null);

  const loadUserList = () => {
    setIsFirmUserLoading(true);
    totalCountEvent('user')
      .then((response) => {
        listUsers({
          status: 'ACTIVE',
          limit: response.data,
          is_confirmed_user: true,
          is_include_groups: false,
          active_groups: false,
          excludeFromLoading: false,
        })
          .then((response) => {
            const firmUsers = response.data?.firm_users?.filter((user) => user.res_type === 'user') || [];
            const transformedUsers = firmUsers.map((user) => ({
              ...user,
              label: `${user.first_name} ${user.last_name}`,
              value: user.firm_user_id,
            }));

            const sortedFirmUsers = sortByKey(transformedUsers, 'label');
            setUserList([...sortedFirmUsers]);
          })
          .catch((err) => console.error(err))
          .finally(() => setIsFirmUserLoading(false));
      })
      .catch((error) => {
        setIsFirmUserLoading(false);
        console.warn(error);
      });
  };

  useEffect(() => {
    loadUserList();
  }, []);

  useEffect(() => {
    if (userList.length > 0 && userDetails?.client_cognito_username) {
      const isUserInList = userList.some((u) => u.value === userDetails.firm_user_id);
      if (isUserInList) {
        setFilters((prev) => ({ ...prev, userFilter: [userDetails.firm_user_id] }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userList, userDetails]);

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {option?.label}
      </>
    );
  };

  const markEmailListAsRead = () => {
    // Extract only email_thread_id
    const threadIds = selectedEmails.map((email) => email.email_thread_id);

    const dataBody = {
      email_thread_ids: threadIds,
    };

    try {
      markEmailsAsRead(dataBody).then((response) => {
        refreshEmails();
        notificationData?.loadNotificationCount();
      });
    } catch (err) {
      console.error('Error marking emails as read:', err);
    }
  };
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <>
      <div className="d-flex align-items-center mb-3 justify-content-between flex-wrap">
        <div className="d-block d-lg-none w-100">
          <Button
            className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            aria-controls="mail-filter-section"
            label="Filters"
          >
            <span
              style={{
                fontSize: 18,
                transition: 'transform 0.2s',
                display: 'inline-block',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <i className="fa fa-solid fa-angle-down"></i>
            </span>
          </Button>
        </div>
        <div
          className={`d-flex flex-wrap align-items-center justify-content-start p-2 p-lg-0 filter-wrap lead-filter autowidth filter-toggle-section filter-section${
            showFilters ? ' show' : ''
          }`}
          id="mail-filter-section"
          aria-hidden={!showFilters}
        >
          <div className="d-flex flex-wrap align-items-center p-2 filter-wraper gap-2">
            <label className="text-bold black-600  me-2 filter-label">Filters:</label>
            {isGlobalMail && (
              <div className="d-flex flex-column filter-wraper">
                <CaseListDropDown
                  value={filters.caseId || caseValue || null}
                  onChange={(data) => {
                    setFilters((prev) => ({ ...prev, caseId: data }));
                    setSelectedEmailThreadPkSk({});
                  }}
                  isClearable
                  placeholder="By Case/Lead"
                  excludeFromLoading={true}
                />
              </div>
            )}
            <div className="d-flex flex-column filter-wraper">
              <MultiSelect
                value={filters.userFilter}
                options={userList}
                onChange={(e) => setFilters((prev) => ({ ...prev, userFilter: [...e.value] }))}
                placeholder="By User"
                className="input-shadow F-size14"
                optionLabel="label"
                optionValue="value"
                display="chip"
                filter
                itemTemplate={assignToItemTemplate}
                panelFooterTemplate={isFirmUserLoading ? LoaderComponent : null}
              />
            </div>
            <div className="d-flex flex-column filter-wraper">
              <MultiSelect
                options={MAPPING_STATUSES}
                value={filters.mappingStatuses}
                onChange={(e) => setFilters((prev) => ({ ...prev, mappingStatuses: [...e.value] }))}
                placeholder="By Mapping Status"
                optionLabel="label"
                optionValue="value"
                display="chip"
                className="input-shadow F-size14"
                filter
                itemTemplate={assignToItemTemplate}
              />
            </div>
            <div className="d-flex flex-column filter-wraper">
              <TimezoneDatePicker
                ref={calendarRef}
                timezone={userDetails?.userDetails?.timezone}
                className="input-shadow filter-calender"
                id="mail-date-filter"
                value={dateFilter}
                onChange={(value) => {
                  onDateChange(value);
                  setSelectedEmailThreadPkSk({});
                }}
                selectionMode="range"
                showIcon
                placeholder="Date"
                isEndOfDay={true}
              />
            </div>
            <div className="d-flex flex-row pointer ms-0 p-2 gap-2">
              <div className="pointer ms-0">
                <i
                  className="fas fa-sync-alt"
                  onClick={() => {
                    if (isGlobalMail) {
                      if (calendarRef?.current?.state?.overlayVisible) {
                        calendarRef.current.state.overlayVisible = false;
                      }
                      onDateChange('');
                      setFilters({
                        caseId: '',
                        mail_date_lower: '',
                        mail_date_upper: '',
                        userFilter: [userDetails.firm_user_id],
                        mappingStatuses: mapping_statuses,
                      });
                    } else {
                      if (calendarRef?.current?.state?.overlayVisible) {
                        calendarRef.current.state.overlayVisible = false;
                      }
                      onDateChange('');
                      setFilters({
                        mail_date_lower: '',
                        mail_date_upper: '',
                        userFilter: [userDetails.firm_user_id],
                        mappingStatuses: mapping_statuses,
                      });
                    }
                    setSelectedEmailThreadPkSk({});
                    setSelectedEmails([]);
                  }}
                ></i>
              </div>
              <Tooltip target=".fa-sync-alt" content="Reset Filters" position="bottom" showDelay={500} />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center my-2 gap-2 flex-wrap">
          <Button
            type="button"
            className="p-button p-component p-button-primary button outline"
            label="Mark as read"
            disabled={!(selectedEmails?.length > 0)}
            onClick={() => markEmailListAsRead()}
          />
          <Button
            type="button"
            className="p-button p-component p-button-primary button outline"
            label="Map to Case/Lead"
            disabled={!(selectedEmails?.length > 0)}
            onClick={() => setShow(true)}
          />
          {isGlobalMail && (
            <>
              {' '}
              <Link className="text-decoration-none" to="/mail-logs">
                <Button label="Mailbox" type="button" className="btn-outline p-button-primary" />
              </Link>
            </>
          )}
          <Button
            label="Compose"
            type="button"
            className="btn-outline p-button-primary"
            icon="pi pi-plus"
            onClick={() => setShowComposeModal(true)}
          />
        </div>
      </div>
    </>
  );
}
